import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import PlayingCard from "./PlayingCard";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, Medal, Award, Star, TrendingUp, ArrowLeft, Timer, ArrowRight, AlertTriangle } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";

const rankColors = {
  1: "text-yellow-400 border-yellow-400/50 bg-yellow-400/10",
  2: "text-gray-300 border-gray-300/50 bg-gray-300/10",
  3: "text-amber-600 border-amber-600/50 bg-amber-600/10",
  4: "text-muted-foreground border-border bg-muted/30"
};

const rankIcons = {
  1: Trophy,
  2: Medal,
  3: Award,
  4: null
};

// Colors for each player in the chart
const playerColors = ["#FFD700", "#C0C0C0", "#CD7F32", "#9CA3AF"];

// Format number with sign
const formatWithSign = (num) => {
  if (num === undefined || num === null) return "0";
  const rounded = Math.round(num * 10) / 10;
  return rounded >= 0 ? `+${rounded}` : `${rounded}`;
};

export default function ResultsModal({ 
  results, 
  onVoteNextRound, 
  onQuit,
  nextRoundVotes = 0,
  totalPlayers = 4,
  hasVoted = false,
  phaseEndTime = null,
  waitingForPlayers = []
}) {
  const [showStats, setShowStats] = useState(false);
  const [countdown, setCountdown] = useState(20);

  // Countdown timer
  useEffect(() => {
    if (phaseEndTime) {
      const endTime = parseFloat(phaseEndTime) * 1000;
      
      const updateCountdown = () => {
        const now = Date.now();
        const remaining = Math.max(0, Math.ceil((endTime - now) / 1000));
        setCountdown(remaining);
      };
      
      updateCountdown();
      const interval = setInterval(updateCountdown, 1000);
      return () => clearInterval(interval);
    }
  }, [phaseEndTime]);

  // Helper to format countdown display
  const getCountdownDisplay = () => {
    if (countdown === 0 && waitingForPlayers.length > 0) {
      return "待機中...";
    }
    return `${countdown}秒`;
  };

  const getCountdownClass = () => {
    if (countdown === 0 && waitingForPlayers.length > 0) {
      return "text-amber-400";
    }
    return countdown <= 5 ? "text-red-400 font-bold" : "";
  };

  if (!results) return null;

  // Prepare chart data for CHIP history - X axis from 0 to max(10, rounds)
  // Y axis shows change from initial chips (0 = starting point)
  const INITIAL_CHIPS = 500;
  
  const prepareChartData = () => {
    const allHistories = results.results?.map(r => r.chip_history || []) || [];
    const maxRounds = Math.max(...allHistories.map(h => h.length), 1);
    const xAxisMax = Math.max(10, maxRounds);
    
    const data = [];
    // Start from 0 (initial point, no change yet)
    const initialPoint = { round: 0 };
    results.results?.forEach(r => {
      initialPoint[r.nickname] = 0;  // Start at 0 (no change from initial)
    });
    data.push(initialPoint);
    
    // Add each round's data as change from initial chips
    for (let i = 0; i < xAxisMax; i++) {
      const point = { round: i + 1 };
      results.results?.forEach(r => {
        const history = r.chip_history || [];
        // Convert absolute chips to change from initial
        point[r.nickname] = history[i] !== undefined ? history[i] - INITIAL_CHIPS : null;
      });
      data.push(point);
    }
    
    return data;
  };

  const chartData = prepareChartData();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-1 sm:p-2 md:p-4" data-testid="results-modal">{/* Backdrop */}
      <div className="absolute inset-0 bg-black/90" />
      
      {/* Modal */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="relative w-full max-w-3xl mx-1 sm:mx-2 bg-card border border-brass/30 rounded-lg sm:rounded-xl shadow-2xl flex flex-col"
        style={{ maxHeight: 'calc(100vh - 32px)', height: 'auto' }}
      >
        <AnimatePresence mode="wait">
          {!showStats ? (
            // Round Results View
            <motion.div
              key="results"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="flex flex-col overflow-hidden"
              style={{ maxHeight: 'calc(100vh - 48px)' }}
            >
              {/* Header - Fixed with button */}
              <div className="p-2 sm:p-3 md:p-4 bg-gradient-to-b from-brass/20 to-transparent border-b border-border flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-1 sm:gap-2">
                  <h2 className="font-display text-base sm:text-lg md:text-2xl text-brass">
                    R{results.round} 結果
                  </h2>
                  {/* Countdown Timer */}
                  <div className="flex items-center gap-0.5 sm:gap-1 text-[10px] sm:text-xs md:text-sm text-muted-foreground">
                    <Timer className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className={getCountdownClass()}>
                      {getCountdownDisplay()}
                    </span>
                  </div>
                </div>
                {/* Button in header */}
                <Button
                  onClick={() => setShowStats(true)}
                  className="bg-brass text-accent-foreground hover:bg-brass/90 font-medium text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-2 h-auto"
                  data-testid="to-stats"
                >
                  <span className="hidden sm:inline">累積結果へ</span>
                  <span className="sm:hidden">次へ</span>
                  <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 ml-1" />
                </Button>
              </div>

              {/* Results - Scrollable with explicit max-height */}
              <div 
                className="flex-1 overflow-y-auto p-2 sm:p-3 md:p-4 space-y-2 sm:space-y-3"
              >
                {results.results?.map((result, index) => {
                  const RankIcon = rankIcons[result.ranking];
                  const colorClass = rankColors[result.ranking] || rankColors[4];
                  
                  const bestHandCards = result.cards?.filter(c => c.is_in_best_hand) || [];
                  const unusedCards = result.cards?.filter(c => !c.is_in_best_hand) || [];
                  
                  return (
                    <motion.div
                      key={result.player_id}
                      initial={{ opacity: 0, x: -30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className={`p-2 sm:p-3 border-2 rounded-lg sm:rounded-xl ${colorClass}`}
                    >
                      {/* Player Header */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-1 sm:gap-2">
                          <span className="font-display text-lg sm:text-xl md:text-2xl font-bold">
                            {result.ranking}位
                          </span>
                          {RankIcon && <RankIcon className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5" />}
                          <span className="font-medium text-xs sm:text-sm md:text-base text-foreground truncate max-w-[60px] sm:max-w-[80px] md:max-w-none">{result.nickname}</span>
                        </div>
                        
                        <Badge className="font-display text-[10px] sm:text-xs md:text-sm px-1.5 sm:px-2 py-0.5 bg-brass/20 text-brass border-brass/30">
                          {result.hand_name}
                        </Badge>
                      </div>
                      
                      {/* Cards and Stats Row */}
                      <div className="flex flex-col sm:flex-row gap-2">
                        {/* Cards Section */}
                        <div className="flex-1 space-y-1">
                          {/* Best Hand Cards */}
                          {bestHandCards.length > 0 && (
                            <div>
                              <div className="text-[8px] sm:text-[10px] text-brass mb-0.5 flex items-center gap-0.5">
                                <Star className="w-2 h-2 fill-brass" />
                                役を構成する5枚
                              </div>
                              <div className="flex gap-0.5 flex-wrap p-1 bg-brass/10 rounded border border-brass/30">
                                {bestHandCards.map((card, cardIndex) => (
                                  <PlayingCard key={`best-${cardIndex}`} suit={card.suit} rank={card.rank} isPublic={card.is_public} size="xs" />
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Unused Cards */}
                          {unusedCards.length > 0 && (
                            <div className="flex items-center gap-1">
                              <div className="flex gap-0.5 opacity-50">
                                {unusedCards.map((card, cardIndex) => (
                                  <PlayingCard key={`unused-${cardIndex}`} suit={card.suit} rank={card.rank} isPublic={card.is_public} size="xs" />
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Stats Section - Detailed breakdown */}
                        <div className="flex-shrink-0 bg-muted/30 rounded-lg p-1.5 sm:p-2 text-[10px] sm:text-xs min-w-0 sm:min-w-[150px] md:min-w-[170px]">
                          {/* Ranking Points (chip_change) */}
                          <div className="flex justify-between items-center mb-0.5">
                            <span className="text-muted-foreground">順位点</span>
                            <span className={result.chip_change >= 0 ? "text-green-400" : "text-red-400"}>
                              {formatWithSign(result.chip_change)}
                            </span>
                          </div>
                          
                          {/* Chip Transfer from trades */}
                          <div className="flex justify-between items-center mb-0.5">
                            <span className="text-muted-foreground">チップ授受</span>
                            <span className={(result.chip_transfer || 0) >= 0 ? "text-green-400" : "text-red-400"}>
                              {formatWithSign(result.chip_transfer || 0)}
                            </span>
                          </div>
                          
                          {/* Handicap */}
                          <div className="flex justify-between items-center mb-0.5">
                            <span className="text-muted-foreground">ハンデ</span>
                            <span className={(result.handicap || 0) >= 0 ? "text-green-400" : "text-red-400"}>
                              {formatWithSign(result.handicap || 0)}
                            </span>
                          </div>
                          
                          {/* Divider */}
                          <div className="border-t border-border/50 my-1"></div>
                          
                          {/* Apparent Rate Change */}
                          <div className="flex justify-between items-center mb-0.5">
                            <span className="text-blue-400 flex items-center gap-0.5">
                              <TrendingUp className="w-2.5 h-2.5" />R
                            </span>
                            <span className="text-blue-400 text-[9px] sm:text-[10px]">
                              {Math.round(result.apparent_before || 0)}→{Math.round(result.apparent || 0)}
                              <span className={`ml-0.5 ${(result.apparent_change || 0) >= 0 ? "text-green-400" : "text-red-400"}`}>
                                ({formatWithSign(result.apparent_change)})
                              </span>
                            </span>
                          </div>
                          
                          {/* Business Acumen Change */}
                          <div className="flex justify-between items-center">
                            <span className="text-amber-400">商才</span>
                            <span className="text-amber-400 text-[9px] sm:text-[10px]">
                              {Math.round(result.biz_before || 0)}→{Math.round(result.biz || 0)}
                              <span className={`ml-0.5 ${(result.biz_change || 0) >= 0 ? "text-green-400" : "text-red-400"}`}>
                                ({formatWithSign(result.biz_change)})
                              </span>
                            </span>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          ) : (
            // Cumulative Stats View
            <motion.div
              key="stats"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="flex flex-col overflow-hidden"
              style={{ maxHeight: 'calc(100vh - 48px)' }}
            >
              {/* Header with countdown and buttons */}
              <div className="p-2 sm:p-3 md:p-4 border-b border-border flex items-center justify-between flex-shrink-0 gap-1 sm:gap-2">
                <div className="flex items-center gap-1 sm:gap-2 min-w-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowStats(false)}
                    className="p-1 sm:p-1.5 h-auto"
                  >
                    <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4" />
                  </Button>
                  <h2 className="font-display text-sm sm:text-lg md:text-xl text-brass truncate">累積結果</h2>
                  {/* Countdown Timer */}
                  <div className="flex items-center gap-0.5 text-[10px] sm:text-xs text-muted-foreground">
                    <Timer className="w-3 h-3" />
                    <span className={getCountdownClass()}>
                      {getCountdownDisplay()}
                    </span>
                  </div>
                </div>
                {/* Action buttons in header */}
                <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                  <Button
                    onClick={onVoteNextRound}
                    disabled={hasVoted}
                    className="bg-brass text-accent-foreground hover:bg-brass/90 font-medium text-[10px] sm:text-xs md:text-sm px-2 sm:px-3 py-1 sm:py-2 h-auto whitespace-nowrap"
                    data-testid="next-round-btn"
                  >
                    <span className="hidden sm:inline">{hasVoted ? `投票済` : `次のラウンドへ`}</span>
                    <span className="sm:hidden">{hasVoted ? `済` : `次へ`}</span>
                    <span className="ml-0.5 sm:ml-1">({nextRoundVotes}/{totalPlayers})</span>
                  </Button>
                  <Button
                    onClick={onQuit}
                    variant="ghost"
                    className="text-muted-foreground hover:text-red-400 text-[10px] sm:text-xs px-2 py-1 h-auto"
                    data-testid="quit-btn"
                  >
                    やめる
                  </Button>
                </div>
              </div>

              {/* Warning banner for disconnected players */}
              {waitingForPlayers.length > 0 && (
                <div className="mx-2 sm:mx-3 md:mx-4 mb-2 p-2 sm:p-3 bg-amber-500/20 border border-amber-500/50 rounded-lg flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-amber-500 flex-shrink-0" />
                  <div className="text-xs sm:text-sm">
                    <span className="font-medium text-amber-500">離脱中: </span>
                    <span className="text-amber-200">{waitingForPlayers.join(', ')}</span>
                    <p className="text-[10px] sm:text-xs text-amber-300/70 mt-0.5">
                      全員が接続するまで次のラウンドは開始されません
                    </p>
                  </div>
                </div>
              )}

              {/* Chart - Scrollable content */}
              <div className="flex-1 overflow-y-auto p-2 sm:p-3 md:p-4">
                <div className="min-h-[180px] sm:min-h-[200px]">
                  <ResponsiveContainer width="100%" height={180}>
                    <LineChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis 
                        dataKey="round" 
                        stroke="#888"
                        tick={{ fontSize: 9 }}
                        domain={[0, 'dataMax']}
                      />
                      <YAxis 
                        stroke="#888"
                        tick={{ fontSize: 9 }}
                        label={{ value: '増減', angle: -90, position: 'insideLeft', fontSize: 9, fill: '#888' }}
                        domain={['dataMin', 'dataMax']}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#1a1a1a', 
                          border: '1px solid #333',
                          borderRadius: '8px',
                          fontSize: '10px'
                        }}
                        labelStyle={{ color: '#C5A059' }}
                        labelFormatter={(value) => `ラウンド ${value}`}
                      />
                      <Legend 
                        wrapperStyle={{ paddingTop: '5px', fontSize: '9px' }}
                      />
                      {results.results?.map((result, index) => (
                        <Line
                          key={result.nickname}
                          type="monotone"
                          dataKey={result.nickname}
                          name={result.nickname}
                          stroke={playerColors[index % playerColors.length]}
                          strokeWidth={2}
                          dot={{ r: 2 }}
                          activeDot={{ r: 4 }}
                          connectNulls={false}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                {/* Current Chip Holdings */}
                <div className="mt-3 sm:mt-4">
                  <h3 className="text-[10px] sm:text-xs text-muted-foreground mb-1.5 sm:mb-2">現在の保有チップ</h3>
                  <div className="grid grid-cols-2 gap-1 sm:gap-1.5 md:gap-2">
                    {results.results?.map((result, index) => (
                      <div 
                        key={result.player_id}
                        className="flex items-center justify-between p-1.5 sm:p-2 rounded-lg bg-muted/30"
                      >
                        <div className="flex items-center gap-1 min-w-0">
                          <div 
                            className="w-2 h-2 rounded-full flex-shrink-0"
                            style={{ backgroundColor: playerColors[index % playerColors.length] }}
                          />
                          <span className="text-[10px] sm:text-xs truncate">{result.nickname}</span>
                        </div>
                        <div className="flex items-center gap-1.5 sm:gap-2 text-[9px] sm:text-[10px] flex-shrink-0">
                          <span className="text-brass font-bold">{result.chips || 500}枚</span>
                          <span className="text-blue-400">R{Math.round(result.apparent || 0)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
